"use client";

import { create, StateCreator } from "zustand";

type Lesson = {
  unitSlug: string;
  lessonSlug: string;
  programmeSlug: string;
  title: string;
  unitTitle: string;
  maxPoints: number;
};

type UserSlice = {
  userProfilePicUrl?: string;
  googleLoginHint?: string;
  setUser: (profilePicUrl?: string) => void;
  setGoogleLoginHint: (loginHint: string) => void;
};

type BrowseSlice = {
  lessons: Lesson[];
  setLessons: (lessons: Lesson[]) => void;
  unsetLessons: () => void;
  lessonSelected: boolean;
  setLessonSelected: () => void;
  isGradeSynced?: boolean;
  toggleGradeSync: (isSynced: boolean) => void;
};

type AddonSlice = {
  courseId?: string;
  itemId?: string;
  addOnToken?: string;
  isInitialised: boolean;
  initialiseAddon: (
    courseId: string,
    itemId: string,
    addOnToken: string,
  ) => void;
};

type GoogleClassroomAddonStore = UserSlice & BrowseSlice & AddonSlice;

const createUserSlice: StateCreator<
  GoogleClassroomAddonStore,
  [],
  [],
  UserSlice
> = (set) => ({
  userProfilePicUrl: undefined,
  googleLoginHint: undefined,
  setUser: (profilePicUrl) => set(() => ({ userProfilePicUrl: profilePicUrl })),
  setGoogleLoginHint: (loginHint) =>
    set(() => ({ googleLoginHint: loginHint })),
});

const lessonEquals = (a: Lesson, b: Lesson) =>
  a.unitSlug === b.unitSlug &&
  a.lessonSlug === b.lessonSlug &&
  a.programmeSlug === b.programmeSlug;

const isLessonNotInList = (lesson: Lesson, existingLessons: Lesson[]) =>
  !existingLessons.some((sl) => lessonEquals(sl, lesson));

const getNewLessons = (newLessons: Lesson[], existingLessons: Lesson[]) =>
  newLessons.filter((l) => isLessonNotInList(l, existingLessons));

const createBrowseSlice: StateCreator<
  GoogleClassroomAddonStore,
  [],
  [],
  BrowseSlice
> = (set) => ({
  lessons: [],
  lessonSelected: false,
  isGradeSynced: true,
  setLessons: (lessons) =>
    set((state) => ({
      lessons: [...state.lessons, ...getNewLessons(lessons, state.lessons)],
    })),
  unsetLessons: () => set(() => ({ lessons: [], lessonSelected: false })),
  toggleGradeSync: () =>
    set((state) => ({ isGradeSynced: !state.isGradeSynced })),
  setLessonSelected: () => set(() => ({ lessonSelected: true })),
});

const createAddonSlice: StateCreator<
  GoogleClassroomAddonStore,
  [],
  [],
  AddonSlice
> = (set) => ({
  courseId: undefined,
  itemId: undefined,
  addOnToken: undefined,
  isInitialised: false,
  initialiseAddon: (courseId, itemId, addOnToken) =>
    set(() => ({ courseId, itemId, addOnToken, isInitialised: true })),
});

export const useGoogleClassroomAddonStore = create<GoogleClassroomAddonStore>()(
  (...a) => ({
    ...createUserSlice(...a),
    ...createBrowseSlice(...a),
    ...createAddonSlice(...a),
  }),
);
